import { toast } from "@credit-acceptance/uikit";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { computeShouldForceExit } from "../../hooks/useForceExitCheck";
import { getErrorMessage } from "../../lib/query/errors";
import { useLockReadOnly } from "../../stores/approvalStore";
import { useForceExitStore } from "../../stores/forceExitStore";
import { fundingFaxResult, sendFundingFaxErrorKey, useSendFundingFax } from "../actions/api/useSendFundingFax";
import { useWorkbenchActions } from "../actions/api/useWorkbenchActions";
import { applicationStatusQuery } from "../review/api/useApplicationStatus";
import { useExitOptions } from "./api/useExitOptions";
import { useMoveAccount } from "./api/useMoveAccount";

interface ExitAccountModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Deal / approval identifier — path param for the options read + save. */
  approvalId: string;
  /**
   * Current status description (from the application-status read). Shown as the
   * read-only "Current Account Status" and passed to the options query as the
   * `currentStatus` the recap-service transitions are computed from.
   */
  currentStatus?: string;
  /** Current phase description (from the application-status read); display only. */
  currentPhase?: string;
  /** Analyst role name — required query param for the options read. */
  roleName?: string;
  /**
   * Saves the applicant data (the same save the footer's Save button runs).
   * Save & Exit awaits this and only proceeds to exit when it resolves
   * `true`; a `false` (validation failure, save error, or a force-exit
   * block) keeps the modal open so the analyst sees why.
   */
  onSaveApplicant: () => Promise<boolean>;
  /**
   * Performs the actual exit side-effects: releases the held VLA lock and
   * navigates to the exit target. Invoked after a Save & Exit (once the
   * applicant save succeeds) or immediately for an Exit-without-saving.
   */
  onExit: () => void | Promise<void>;
}

/**
 * Picks the value to seed a dropdown with: the first non-empty preferred value,
 * or the first available option if none is given. Callers are expected to
 * union the seeded value into the rendered option list themselves (see
 * `withCurrentOption`) so the Select never ends up holding a value with no
 * matching item.
 * @param {string[] | undefined} list - The available option values.
 * @param {...(string | undefined)} preferred - Candidate values in priority order.
 * @returns {string} The value to seed the dropdown with.
 */
function pickSeed(list: string[] | undefined, ...preferred: Array<string | undefined>): string {
  const match = preferred.find((value) => value !== undefined && value !== "");
  return match ?? list?.[0] ?? "";
}

// Ensures `current` shows up as a selectable item even when the BFF's
// transition list doesn't include it (e.g. the deal's current status/phase
// isn't itself an allowed transition target).
function withCurrentOption(list: string[] | undefined, current: string): string[] {
  const base = list ?? [];
  return current && !base.includes(current) ? [current, ...base] : base;
}

// Phase auto-defaulting only applies to deals that are currently sitting in
// the pre-approval phase — moving to/from these two values doesn't make
// sense for a deal that's already elsewhere in the pipeline.
const PRE_APPROVAL_PENDING_PHASE = "Pre-approval Pending";
const PRE_APPROVAL_COMPLETED_PHASE = "Pre-approval Completed";

// "Exit the Account" modal, in parity with Step 6's exit screen. Shows the
// deal's current status/phase, lets the analyst pick the next status/phase to
// move the account to (options fetched from the Workbench exit-options BFF), and
// confirms the exit. Ticking "Exit without saving" skips the applicant save but
// still persists the status/phase move.
//
// Confirm always persists the selected status/phase via WorkbenchExitApi's
// move-account (see useMoveAccount) before the release-lock + navigate
// side-effects, handled by the `onExit` callback supplied by the page, run.
function ExitAccountModal({
  open,
  onOpenChange,
  approvalId,
  currentStatus,
  currentPhase,
  roleName,
  onSaveApplicant,
  onExit,
}: ExitAccountModalProperties) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const readOnly = useLockReadOnly();
  const setShouldForceExit = useForceExitStore((state) => state.setShouldForceExit);
  const [nextStatus, setNextStatus] = useState("");
  const [nextPhase, setNextPhase] = useState("");
  // Tracks whether the analyst picked the phase themselves, so the
  // Save & Exit / Exit-without-saving auto-default below stops overriding
  // their choice the moment they touch the dropdown.
  const [phaseManuallySet, setPhaseManuallySet] = useState(false);
  const [exitWithoutSaving, setExitWithoutSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const { mutateAsync: moveAccount } = useMoveAccount();
  const sendFundingFax = useSendFundingFax(approvalId);
  const isSendingFaxReference = useRef(false);
  const actionsQuery = useWorkbenchActions(approvalId, roleName ?? "");
  const canSendFundingFax =
    actionsQuery.isSuccess && actionsQuery.data.some((action) => action.id === "send-funding-fax");

  // Only fetch options while the modal is open and the required inputs exist.
  const {
    data: options,
    isLoading,
    isError,
    error,
  } = useExitOptions(approvalId, currentStatus ?? "", roleName ?? "", { enabled: open });

  // Seed the dropdowns with the deal's current status/phase as soon as the
  // modal opens — not gated on the options fetch landing — so a slow or
  // failed options request never leaves confirm permanently disabled. Once
  // the response does land it's folded in too, without clobbering a
  // selection the analyst changed.
  useEffect(() => {
    if (!open) {
      return;
    }
    setNextStatus((previous) => previous || pickSeed(options?.nextStatusList, currentStatus, options?.currentStatus));
    setNextPhase((previous) => previous || pickSeed(options?.phasesList, currentPhase, options?.currentPhase));
  }, [open, options, currentStatus, currentPhase]);

  // For deals currently in "Pre-approval Pending", default the phase to move
  // to based on the action: "Pre-approval Completed" for Save & Exit, back to
  // "Pre-approval Pending" for Exit without saving. Stops re-defaulting the
  // instant the analyst picks a phase themselves, so their choice sticks
  // regardless of how they subsequently toggle the checkbox.
  useEffect(() => {
    if (!open || phaseManuallySet || currentPhase !== PRE_APPROVAL_PENDING_PHASE) {
      return;
    }
    setNextPhase(exitWithoutSaving ? PRE_APPROVAL_PENDING_PHASE : PRE_APPROVAL_COMPLETED_PHASE);
  }, [open, exitWithoutSaving, currentPhase, phaseManuallySet]);

  // Reset transient state whenever the modal closes so reopening starts clean.
  useEffect(() => {
    if (open) {
      return;
    }
    setNextStatus("");
    setNextPhase("");
    setPhaseManuallySet(false);
    setExitWithoutSaving(false);
    setIsSubmitting(false);
    setSaveError(undefined);
  }, [open]);

  const handleConfirm = useCallback(async () => {
    // isSubmitting is React state, not synchronous — a second click before
    // the first render lands would otherwise slip past the disabled check.
    if (isSubmitting || isSendingFaxReference.current) {
      return;
    }
    setIsSubmitting(true);
    setSaveError(undefined);
    try {
      // Fresh status check runs first, before save, for both Save & Exit and
      // Exit without saving — a stale/cached status could hide a force-exit
      // that happened after this modal opened.
      let isForceExit = false;
      try {
        const status = await queryClient.fetchQuery({ ...applicationStatusQuery(approvalId), staleTime: 0 });
        isForceExit = computeShouldForceExit({ isPreApprovalVoid: status.isPreApprovalVoid, readOnly });
        setShouldForceExit(isForceExit);
      } catch (statusCheckError) {
        setSaveError(getErrorMessage(statusCheckError));
        return;
      }

      if (!exitWithoutSaving) {
        const saved = await onSaveApplicant();
        if (!saved) {
          // Signers' own driver/secondSigner banners already show the specific
          // reason (validation, save error, or force-exit warning) — this is
          // just the modal-local summary. Stay open; don't exit.
          setSaveError(t("exit.saveError"));
          return;
        }
      }

      if (!isForceExit) {
        try {
          await moveAccount({
            approvalId,
            targetStatus: nextStatus,
            targetPhase: nextPhase,
            currentStatus: currentStatus ?? "",
            currentPhase: currentPhase ?? "",
          });
        } catch (moveAccountError) {
          // useMoveAccount throws this same message when a force-exit lands in
          // the race window between the check above and this request — that's
          // not a failure to report, it's the analyst trying to leave. Let
          // them out without persisting the move rather than trapping them
          // behind an error.
          const isMoveAccountForceExit =
            moveAccountError instanceof Error && moveAccountError.message === t("workbench.saveAbortedForceExit");
          if (!isMoveAccountForceExit) {
            // Gate the redirect — an analyst who just saved (or skipped saving)
            // should still see why the exit itself didn't go through.
            setSaveError(t("exit.moveAccountError"));
            return;
          }
        }
      }
      // Close before onExit — redirectToOriginSite no-ops outside deployed CAPS
      // hosts (local dev, previews), so the dialog must close itself rather than
      // rely on the page navigation to dismiss it.
      onOpenChange(false);
      await onExit();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    exitWithoutSaving,
    onSaveApplicant,
    queryClient,
    approvalId,
    readOnly,
    setShouldForceExit,
    moveAccount,
    nextStatus,
    nextPhase,
    currentStatus,
    currentPhase,
    onExit,
    onOpenChange,
    t,
  ]);

  const handleSendFundingFax = useCallback(async () => {
    // isPending is React Query state, delivered on a later tick — a second
    // click before that render lands would otherwise slip past the disabled
    // check and fax the dealer twice.
    if (isSendingFaxReference.current || isSubmitting || readOnly || !canSendFundingFax) {
      return;
    }
    isSendingFaxReference.current = true;
    try {
      const { sent, key } = fundingFaxResult(await sendFundingFax.mutateAsync());
      if (sent) {
        toast.success(t(key));
      } else {
        toast.error(t(key));
      }
    } catch (faxError) {
      toast.error(t(sendFundingFaxErrorKey(faxError)));
    } finally {
      isSendingFaxReference.current = false;
    }
  }, [sendFundingFax, isSubmitting, readOnly, canSendFundingFax, t]);

  const statusList = useMemo(() => withCurrentOption(options?.nextStatusList, nextStatus), [options, nextStatus]);
  const phaseList = useMemo(() => withCurrentOption(options?.phasesList, nextPhase), [options, nextPhase]);
  // Both selections are always required — move-account persists them
  // regardless of exitWithoutSaving — so confirm stays disabled until they
  // exist. Deliberately not gated on isLoading: they're seeded from
  // currentStatus/currentPhase as soon as the modal opens, so a hung or
  // failed options fetch doesn't trap the analyst behind a disabled button.
  const confirmDisabled = isSubmitting || sendFundingFax.isPending || !nextStatus || !nextPhase;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="gap-0 overflow-hidden p-0 sm:max-w-md">
        {/* Header — navy bar with title + close. */}
        <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">{t("exit.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("exit.description")}</DialogDescription>
          <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors">
            <X className="size-4.5" />
            <span className="sr-only">{t("exit.close")}</span>
          </DialogClose>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-3 p-4">
          {/* Current account status (read-only) */}
          <section>
            <h4 className="text-primary mb-1 text-xs font-semibold">{t("exit.currentStatus.heading")}</h4>
            <div className="border-border bg-muted grid grid-cols-2 gap-2 rounded-md border p-2">
              <div>
                <div className="text-muted-foreground text-xs">{t("exit.fields.status")}</div>
                <div className="text-primary text-xs">{currentStatus || t("exit.notAvailable")}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">{t("exit.fields.phase")}</div>
                <div className="text-primary text-xs">{currentPhase || t("exit.notAvailable")}</div>
              </div>
            </div>
          </section>

          {/* Move account to (editable) */}
          <section>
            <h4 className="text-primary mb-1 text-xs font-semibold">{t("exit.moveTo.heading")}</h4>
            {isError ? (
              <p role="alert" className="text-destructive text-xs">
                {t("exit.moveTo.loadError", { message: getErrorMessage(error) })}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-muted-foreground mb-1 text-xs font-normal">{t("exit.fields.status")}</Label>
                  <Select value={nextStatus} onValueChange={setNextStatus} disabled={isLoading}>
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue placeholder={isLoading ? t("exit.moveTo.loading") : t("exit.moveTo.selectStatus")} />
                    </SelectTrigger>
                    <SelectContent>
                      {statusList.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-muted-foreground mb-1 text-xs font-normal">{t("exit.fields.phase")}</Label>
                  <Select
                    value={nextPhase}
                    onValueChange={(value) => {
                      setPhaseManuallySet(true);
                      setNextPhase(value);
                    }}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue placeholder={isLoading ? t("exit.moveTo.loading") : t("exit.moveTo.selectPhase")} />
                    </SelectTrigger>
                    <SelectContent>
                      {phaseList.map((phase) => (
                        <SelectItem key={phase} value={phase}>
                          {phase}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </section>

          {/* Exit without saving */}
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
              className="bg-white"
              checked={exitWithoutSaving}
              onCheckedChange={(checked) => setExitWithoutSaving(checked === true)}
            />
            <span className="text-primary text-xs">{t("exit.exitWithoutSaving")}</span>
          </label>
        </div>

        {/* Footer */}
        <div className="bg-muted flex flex-col gap-2 border-t px-4 py-3">
          {saveError && (
            <p role="alert" className="text-destructive text-xs">
              {saveError}
            </p>
          )}
          <Button type="button" size="sm" disabled={confirmDisabled} onClick={() => void handleConfirm()}>
            {isSubmitting && <Loader2 className="size-4 animate-spin" />}
            {exitWithoutSaving ? t("exit.exit") : t("exit.saveAndExit")}
          </Button>
          <div className="flex gap-2">
            {canSendFundingFax && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="flex-1"
                disabled={isSubmitting || readOnly || sendFundingFax.isPending}
                aria-busy={sendFundingFax.isPending || undefined}
                onClick={() => void handleSendFundingFax()}
              >
                {sendFundingFax.isPending && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
                {t("exit.sendFundingFax")}
              </Button>
            )}
            <DialogClose asChild>
              <Button type="button" variant="outline" size="sm" className="flex-1" disabled={isSubmitting}>
                {t("exit.cancel")}
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExitAccountModal;
